
import { useAuth, PermissionPayload } from "../../modules/auth";
import CrudArtEngenheiroTransportador from "../../components/transportador/art/CrudArtEngenheiroTransportador.vue";
import FilaRntrcPainel from "../../components/rntrc/FilaRntrcPainel.vue";
import {
  useDashboardService,
  DashboarPayload,
  OrgaoRoboStatusPayload
} from "../../modules/business/cliente/dashboardBusiness";
import { defineComponent, reactive, computed, onMounted, ref } from "vue";
import { useToast } from "../../modules/message";
import { PaginationResult, defaultResults } from "../../pagination";
import {
  useOrgaoAetService,
  useOrgaoArtService
} from "../../modules/business/common/commonBusiness";

interface RobosFilasState {
  entity: DashboarPayload | null;
  page: number;
  responseConsulta: boolean;
  responseCadastro: boolean;
  results: PaginationResult<DashboarPayload>;
  loadingFila: boolean;
  orgaosRobo: OrgaoRoboStatusPayload[];
  loadingOrgaos: boolean;
  saldoAntiCaptcha: string | null;
  saldoHcaptcha: string | null;
}

export default defineComponent({
  components: {
    CrudArtEngenheiroTransportador,
    FilaRntrcPainel
  },
  setup() {
    const state = reactive<RobosFilasState>({
      entity: null,
      page: 0,
      responseConsulta: false,
      responseCadastro: false,
      results: defaultResults(),
      loadingFila: false,
      orgaosRobo: [],
      loadingOrgaos: false,
      saldoAntiCaptcha: null,
      saldoHcaptcha: null
    });

    // o painel da Fila ARTS e o proprio CrudArtEngenheiroTransportador; o refresh do cabecalho
    // chama a busca dele
    const filaArtRef = ref<InstanceType<
      typeof CrudArtEngenheiroTransportador
    > | null>(null);

    // painel da Fila RNTRC (as duas filas: solicitação e monitoramento do pagamento)
    const filaRntrcRef = ref<InstanceType<typeof FilaRntrcPainel> | null>(null);

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const executeBuscaStatusConsulta = async () => {
      const { data, isRoboConsultaHabilitado } = useDashboardService();
      await isRoboConsultaHabilitado();
      state.responseConsulta = data.value;
      return data.value;
    };

    const executeBuscaStatusCadastro = async () => {
      const { data, isRoboCadastroHabilitado } = useDashboardService();
      await isRoboCadastroHabilitado();
      state.responseCadastro = data.value;
      return data.value;
    };

    const executeDesligarRoboConsulta = async () => {
      try {
        await useDashboardService().desligarRoboConsulta();
        useToast().success("Robos desligados com sucesso");
        executeBuscaStatusConsulta();
      } catch {
        useToast().error("Falha ao desligar os Robos");
      }
    };

    const executeLigarRoboConsulta = async () => {
      try {
        await useDashboardService().ligarRoboConsulta();
        useToast().success("Robos ligados com sucesso");
        executeBuscaStatusConsulta();
      } catch {
        useToast().error("Falha ao ligar os Robos");
      }
    };

    const executeDesligarRoboCadastro = async () => {
      try {
        await useDashboardService().desligarRoboCadastro();
        useToast().success("Robos desligados com sucesso");
        executeBuscaStatusCadastro();
      } catch {
        useToast().error("Falha ao desligar os Robos");
      }
    };

    const executeLigarRoboCadastro = async () => {
      try {
        await useDashboardService().ligarRoboCadastro();
        useToast().success("Robos ligados com sucesso");
        executeBuscaStatusCadastro();
      } catch {
        useToast().error("Falha ao ligar os Robos");
      }
    };

    const executeBuscaSaldoAntiCaptcha = async () => {
      try {
        const { data, getSaldoAntiCaptcha } = useDashboardService();
        await getSaldoAntiCaptcha();
        state.saldoAntiCaptcha = data.value.balance;
        return state.saldoAntiCaptcha;
      } catch (error) {
        useToast().error("Falha ao buscar saldo AntiCaptcha");
        return null;
      }
    };

    const executeBuscaSaldoHcaptcha = async () => {
      try {
        const { data, getSaldoHcaptcha } = useDashboardService();
        await getSaldoHcaptcha();
        state.saldoHcaptcha = data.value.saldo;
        return state.saldoHcaptcha;
      } catch (error) {
        useToast().error("Falha ao buscar saldo Hcaptcha");
        return null;
      }
    };

    // Tabelinha separada do controle geral (liga/desliga tudo de uma vez),
    // ao lado da tabela por órgão dentro do mesmo painel "Controle de Robôs
    // por Órgão" — mesmo formato (nome colorido + switch) da tabela por
    // órgão. Título da tabela é "Geral"; o nome da linha descreve o que ela
    // controla.
    const controleGeralRows = computed<OrgaoRoboStatusPayload[]>(() => [
      {
        nome: "Robôs Consulta e Cadastro",
        consultaHabilitado: state.responseConsulta,
        cadastroHabilitado: state.responseCadastro
      }
    ]);

    const toggleConsultaGeral = (valor: boolean) => {
      return valor ? executeLigarRoboConsulta() : executeDesligarRoboConsulta();
    };

    const toggleCadastroGeral = (valor: boolean) => {
      return valor ? executeLigarRoboCadastro() : executeDesligarRoboCadastro();
    };

    const executeSearchFilaRobo = () => {
      const { data, getDadosFilaRobo } = useDashboardService();
      state.loadingFila = true;
      return getDadosFilaRobo(state.page)
        .then(() => {
          if (data.value && data.value.content) {
            const resultTratado: DashboarPayload[] = data.value.content.map(
              (item: DashboarPayload, index: number) => {
                item.groupIndex = index;
                return item;
              }
            );
            data.value.content = resultTratado;
            state.results = data.value;
          } else {
            state.results = defaultResults();
          }
        })
        .finally(() => {
          state.loadingFila = false;
        });
    };

    const executeBuscaOrgaosRobo = () => {
      const { data, getOrgaosRoboStatus } = useDashboardService();
      state.loadingOrgaos = true;
      return getOrgaosRoboStatus()
        .then(() => {
          state.orgaosRobo = Array.isArray(data.value) ? data.value : [];
        })
        .finally(() => {
          state.loadingOrgaos = false;
        });
    };

    const orgaoStatusClass = (orgao: OrgaoRoboStatusPayload) => {
      const consulta = !!orgao.consultaHabilitado;
      const cadastro = !!orgao.cadastroHabilitado;
      if (consulta && cadastro) {
        return "orgao-status-on";
      }
      if (!consulta && !cadastro) {
        return "orgao-status-off";
      }
      return "orgao-status-mixed";
    };

    const toggleConsultaOrgao = (
      orgao: OrgaoRoboStatusPayload,
      valor: boolean
    ) => {
      if (!orgao.idOrgao) {
        return;
      }
      const service = useDashboardService();
      const acao = valor
        ? service.ligarRoboConsultaOrgao(orgao.idOrgao)
        : service.desligarRoboConsultaOrgao(orgao.idOrgao);
      acao
        .then(() => {
          useToast().success(
            `Robô de Consulta ${valor ? "ligado" : "desligado"} para ${
              orgao.sigla
            }`
          );
        })
        .finally(() => {
          executeBuscaOrgaosRobo();
        });
    };

    const toggleCadastroOrgao = (
      orgao: OrgaoRoboStatusPayload,
      valor: boolean
    ) => {
      if (!orgao.idOrgao) {
        return;
      }
      const service = useDashboardService();
      const acao = valor
        ? service.ligarRoboCadastroOrgao(orgao.idOrgao)
        : service.desligarRoboCadastroOrgao(orgao.idOrgao);
      acao
        .then(() => {
          useToast().success(
            `Robô de Cadastro ${valor ? "ligado" : "desligado"} para ${
              orgao.sigla
            }`
          );
        })
        .finally(() => {
          executeBuscaOrgaosRobo();
        });
    };

    const getIdOrgaoAetBySigla = (sigla?: string | null) => {
      if (!sigla) {
        return null;
      }
      return (
        useOrgaoAetService()
          .getOrgaosAet()
          .find((o) => o.sigla === sigla)?.idOrgao ?? null
      );
    };

    const getIdOrgaoArtBySigla = (sigla?: string | null) => {
      if (!sigla) {
        return null;
      }
      return (
        useOrgaoArtService()
          .getOrgaosArt()
          .find((o) => o.sigla === sigla)?.idOrgao ?? null
      );
    };

    const getFila = (e?: DashboarPayload) => {
      return useDashboardService().getFila(e);
    };

    const getSigla = (e?: DashboarPayload) => {
      return useDashboardService().getSigla(e);
    };

    const getStatusRobo = (e?: DashboarPayload) => {
      return useDashboardService().getStatusRobo(e);
    };

    const getQuantidade = (e?: DashboarPayload) => {
      return useDashboardService().getQuantidade(e);
    };

    onMounted(() => {
      executeBuscaStatusCadastro();
      executeBuscaStatusConsulta();
      executeSearchFilaRobo();
      executeBuscaOrgaosRobo();
      executeBuscaSaldoAntiCaptcha();
      executeBuscaSaldoHcaptcha();
    });

    return {
      state,
      filaArtRef,
      filaRntrcRef,
      amIAllowed,
      executeBuscaStatusCadastro,
      executeBuscaStatusConsulta,
      executeDesligarRoboConsulta,
      executeLigarRoboConsulta,
      executeDesligarRoboCadastro,
      executeLigarRoboCadastro,
      executeBuscaSaldoAntiCaptcha,
      executeBuscaSaldoHcaptcha,
      controleGeralRows,
      toggleConsultaGeral,
      toggleCadastroGeral,
      executeSearchFilaRobo,
      executeBuscaOrgaosRobo,
      orgaoStatusClass,
      getIdOrgaoAetBySigla,
      getIdOrgaoArtBySigla,
      toggleConsultaOrgao,
      toggleCadastroOrgao,
      getFila,
      getSigla,
      getStatusRobo,
      getQuantidade
    };
  }
});
